import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Avatar, ProgressBar } from 'components/common';
import { Box } from 'components/common/box';
import { ConversationAnalysisSentimentLabels } from '.';

import type { AchievementDiscussionAnalysisResult } from 'shared-types';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  summary: AchievementDiscussionAnalysisResult['executiveSummary'];
}

const ConversationAnalysisSummary = ({ summary }: Props) => {
  const { messages, theme } = useReduxState();

  const sections: {
    icon: IconProp;
    iconBackgroundColor: string;
    iconColor: string;
    items: string[];
    title: string;
  }[] = [
    {
      icon: 'lightbulb',
      iconBackgroundColor: theme.lightGrey,
      iconColor: theme.textColorLight,
      items: summary?.keyFindings,
      title: messages.achievementConversationAnalysesKeyFindings
    },
    {
      icon: 'check-circle',
      iconBackgroundColor: theme.successLightColor,
      iconColor: theme.successLightTextColor,
      items: summary?.topStrengths,
      title: messages.achievementConversationAnalysesStrengths
    },
    {
      icon: 'exclamation-triangle',
      iconBackgroundColor: theme.dangerLightColor,
      iconColor: theme.dangerLightTextColor,
      items: summary?.topConcerns,
      title: messages.achievementConversationAnalysesConcerns
    },
    {
      icon: 'flag',
      iconBackgroundColor: theme.warningLightColor,
      iconColor: theme.warningLightTextColor,
      items: summary?.recommendations,
      title: messages.achievementConversationAnalysesRecommendedPriorities
    }
  ];

  return (
    <div className="summary">
      <Box title={messages.commonSummary}>
        <div className="feel">
          <h4 className="heading">
            {messages.achievementConversationAnalysesGeneralTone}
          </h4>

          <div className="labels">
            <ConversationAnalysisSentimentLabels
              negative={summary?.sentiment.negative}
              neutral={summary?.sentiment.neutral}
              positive={summary?.sentiment.positive}
            />
          </div>

          <div className="progress">
            <ProgressBar
              progresses={[
                {
                  color: theme.successColor,
                  progress: summary?.sentiment.positive * 0.01
                },
                {
                  color: 'rgba(0, 0, 0, 0.4)',
                  progress:
                    (summary?.sentiment.positive + summary?.sentiment.neutral) *
                    0.01
                },
                {
                  color: theme.dangerColor,
                  progress:
                    (summary?.sentiment.positive +
                      summary?.sentiment.neutral +
                      summary?.sentiment.negative) *
                    0.01
                }
              ]}
              progressStyle={{ borderRadius: 20, height: 10 }}
            />
          </div>
        </div>

        <div className="sections">
          {sections.map((section, sectionIndex) => (
            <div className="section" key={sectionIndex}>
              <div className="section-header">
                <div className="section-icon">
                  <Avatar
                    backgroundColor={section.iconBackgroundColor}
                    color={section.iconColor}
                    fontSize="1.1rem"
                    icon={section.icon}
                    padding="7px 0"
                  />
                </div>
                <h4 className="section-title">{section.title}</h4>
              </div>
              {section.items?.length ? (
                <ul className="section-items">
                  {section.items.map((item, itemIndex) => (
                    <li className="section-item" key={itemIndex}>
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="section-empty">{messages.commonNoData}</div>
              )}
            </div>
          ))}
        </div>
      </Box>

      <style jsx>{`
        .heading {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          margin: 0 0 15px;
        }
        .progress {
          margin-top: 10px;
        }
        .sections {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-top: 40px;
        }
        .section {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
          width: 100%;
        }
        .section-header {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .section-title {
          color: ${theme.textColor};
          font-size: 1.6rem;
          margin: 0;
        }
        .section-items {
          margin: 10px 0 0;
          padding-left: 22px;
        }
        .section-item {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          padding: 5px 0;
        }
        .section-empty {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          margin-top: 10px;
        }
        @media (min-width: ${breakpoints.large}px) {
          .section {
            width: calc(50% - 10px);
          }
        }
      `}</style>
    </div>
  );
};

export default ConversationAnalysisSummary;
